/**
 * Single source of truth for how the SPA reaches its own backend.
 *
 * Everything here derives from `window.location` at RUNTIME, never from a
 * build-time hostname. That matters because the same bundle is served from
 * several origins (app.steamroomtrading.com, app.fzntrading.com, sandbox.…),
 * and `VITE_*` values are baked in at build time — one bundle cannot carry a
 * different hostname per origin.
 *
 * Django is reached SAME-ORIGIN through the nginx `/api/*` proxy in this
 * container, so the base is relative in production builds. Same-origin is what
 * lets the HttpOnly session cookie work across two apexes: a cookie is scoped to
 * one registrable domain, so app.fzntrading.com could never receive a cookie
 * scoped to .steamroomtrading.com.
 */

/** True when the page is served over HTTPS. */
function isSecureOrigin(): boolean {
  return window.location.protocol === 'https:';
}

/**
 * Base URL for API calls.
 *
 * Empty string = relative = same-origin, which is what the nginx proxy serves.
 * axios treats an empty `baseURL` as relative, so `/api/...` paths resolve
 * against the page origin.
 *
 * `VITE_API_BASE_URL` remains supported as an escape hatch for local dev
 * against a remote backend, and is the fallback when there is no proxy (plain
 * `vite dev` without the proxy configured).
 */
export const API_BASE_URL: string = import.meta.env.PROD
  ? ''
  : import.meta.env.VITE_API_BASE_URL || 'http://127.0.0.1:8000';

/** Production Django (`config.settings.production`). */
export const CSRF_COOKIE_PROD = '__Secure-csrftoken';
/** Staging Django (`config.settings.staging`). */
export const CSRF_COOKIE_STAGING = '__Secure-staging-csrftoken';

function cookieIsSet(name: string): boolean {
  if (typeof document === 'undefined') return false;
  return document.cookie.split('; ').some((part) => part.startsWith(`${name}=`));
}

/**
 * Name of the CSRF cookie the backend behind THIS origin issues.
 *
 * Portal renames `CSRF_COOKIE_NAME` per environment so staging and production
 * stop sharing one cookie (same name + different SECRET_KEY = each environment
 * silently logging you out of the other). The SPA reads this cookie BY NAME.
 *
 * The name must follow the BACKEND, not the hostname. Sandbox's nginx
 * `PORTAL_UPSTREAM` is an ops switch: it currently points at production
 * (`portal.steamroomtrading.com`). A hostname of `sandbox.` used to mean
 * staging Django, so we looked up `__Secure-staging-csrftoken` and every
 * login POST 403'd against production's `__Secure-csrftoken`.
 *
 * Prefer the cookie Django actually set (after `GET /api/auth/csrf/`). Fall
 * back to hostname only when neither is present: `staging.` still means
 * staging Django; everything else, including `sandbox.`, means production.
 *
 * The `__Secure-` prefix requires HTTPS, so plain-http origins get the bare name.
 */
export function csrfCookieName(): string {
  if (!isSecureOrigin()) return 'csrftoken';
  // Production first: sandbox may still hold a leftover staging cookie from
  // before PORTAL_UPSTREAM flipped. GET /api/auth/csrf/ then sets the prod
  // name, and that is the one the next POST must echo.
  if (cookieIsSet(CSRF_COOKIE_PROD)) return CSRF_COOKIE_PROD;
  if (cookieIsSet(CSRF_COOKIE_STAGING)) return CSRF_COOKIE_STAGING;
  const host = window.location.hostname;
  if (host.startsWith('staging.')) return CSRF_COOKIE_STAGING;
  return CSRF_COOKIE_PROD;
}

/**
 * Absolute WebSocket URL for a same-origin path.
 *
 * Built from `window.location` so the scheme and host always match the page.
 * The previous implementation string-stripped the API base URL; with a relative
 * base that yielded `ws:///ws/streams/` — empty host plus the insecure scheme
 * on an HTTPS page, which browsers block as mixed content.
 *
 * @param path Absolute path beginning with `/`, e.g. `/ws/streams/`.
 */
export function webSocketUrl(path: string): string {
  const scheme = isSecureOrigin() ? 'wss' : 'ws';
  return `${scheme}://${window.location.host}${path}`;
}

/**
 * Read the CSRF token from the cookie, for raw `fetch()` callers.
 *
 * axios attaches `X-CSRFToken` automatically via `xsrfCookieName`; callers that
 * bypass axios must read it themselves.
 */
export function getCsrfToken(): string | null {
  const name = csrfCookieName();
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}
